"use client";

import { useRef, useState } from "react";
import { Loader2 } from "@/components/icons";
import { useI18n } from "@/lib/i18n/I18nProvider";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { PasswordInput } from "./PasswordInput";
import { ViewHeader } from "./ViewHeader";
import { loginErrorClasses, loginLabelClasses, loginPrimaryButtonClasses } from "./styles";
import type { ViewProps } from "./types";

export function ResetPasswordView({ setView }: ViewProps) {
  const { t } = useI18n();
  const pass1Ref = useRef<HTMLInputElement>(null);
  const pass2Ref = useRef<HTMLInputElement>(null);
  const [pass1, setPass1] = useState("");
  const [pass2, setPass2] = useState("");
  const [errors, setErrors] = useState({ pass1: "", pass2: "" });
  const [isLoading, setIsLoading] = useState(false);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const newErrors = {
      pass1: pass1.length < 8 ? t("login.errorLength") : "",
      pass2: pass1 !== pass2 ? t("login.errorMismatch") : "",
    };
    if (newErrors.pass1 || newErrors.pass2) {
      setErrors(newErrors);
      (newErrors.pass1 ? pass1Ref : pass2Ref).current?.focus();
      return;
    }

    setIsLoading(true);
    // Restablecimiento simulado (prototipo sin backend).
    setTimeout(() => setView("forgot_success"), 1000);
  }

  const campos = [
    { id: "pass1", ref: pass1Ref, label: t("login.newPasswordLabel"), value: pass1, set: setPass1, error: errors.pass1 },
    { id: "pass2", ref: pass2Ref, label: t("login.confirmPasswordLabel"), value: pass2, set: setPass2, error: errors.pass2 },
  ];

  return (
    <div className="flex w-full flex-col">
      <ViewHeader title={t("login.newPasswordTitle")} subtitle={t("login.newPasswordSubtitle")} />

      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
        {campos.map((c, i) => (
          <div key={c.id} className="flex flex-col gap-2">
            <Label htmlFor={c.id} className={loginLabelClasses}>
              {c.label}
            </Label>
            <PasswordInput
              ref={c.ref}
              id={c.id}
              autoComplete="new-password"
              autoFocus={i === 0}
              value={c.value}
              onChange={(e) => {
                c.set(e.target.value);
                setErrors({ pass1: "", pass2: "" });
              }}
              disabled={isLoading}
              hasError={!!c.error}
              aria-invalid={!!c.error}
              aria-describedby={c.error ? `${c.id}-error` : undefined}
            />
            {c.error && (
              <p id={`${c.id}-error`} className={loginErrorClasses}>
                {c.error}
              </p>
            )}
          </div>
        ))}

        <Button type="submit" disabled={isLoading} aria-busy={isLoading} className={cn(loginPrimaryButtonClasses, "mt-4")}>
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>{t("login.resetting")}</span>
            </>
          ) : (
            t("login.resetButton")
          )}
        </Button>
      </form>
    </div>
  );
}
